import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, IconButton, Radio, Tooltip, Alert, CircularProgress, Snackbar, Button, Box, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Plus, Trash2, Save, SaveAll } from 'lucide-react';
import { useIntl } from 'react-intl';
import AppConnectorService from '../../services/appconnector.service';

const normalizeVM = (m) => ({
  id: m.id,
  sourceValue: m.sourcevalue ?? m.sourceValue ?? m.source_value ?? '',
  targetValue: m.targetvalue ?? m.targetValue ?? m.target_value ?? '',
  isDefault: m.isdefault ?? m.isDefault ?? m.is_default ?? false,
  displayOrder: m.displayorder ?? m.displayOrder ?? m.display_order ?? 0,
  isActive: m.isactive ?? m.isActive ?? m.is_active ?? true,
});

const ValueMappingEditor = ({ endpointId, profileId, ruleId, targetFieldName, onUpdate }) => {
  const intl = useIntl();
  const tr = (id, values) => intl.formatMessage({ id }, values);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [newRow, setNewRow] = useState({ sourceValue: '', targetValue: '', isDefault: false });
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [dirtyIds, setDirtyIds] = useState(new Set()); // IDs de linhas com alterações não salvas
  const [savingAll, setSavingAll] = useState(false);

  useEffect(() => {
    if (ruleId) loadMappings();
  }, [ruleId]);

  const loadMappings = () => {
    setLoading(true);
    AppConnectorService.getValueMappings(endpointId, profileId, ruleId)
      .then(r => {
        if (r.data.success) {
          setMappings((r.data.data || []).map(normalizeVM));
        }
      })
      .finally(() => setLoading(false));
  };

  const showToast = (message, severity = 'error') => setToast({ open: true, message, severity });

  const extractError = (err) => {
    if (err?.response?.data?.errors?.length > 0) return err.response.data.errors[0];
    if (err?.response?.data?.message) return err.response.data.message;
    return tr('appconnector.vm.error.generic');
  };

  const handleAdd = () => {
    if (!newRow.sourceValue.trim()) { showToast(tr('appconnector.vm.error.source.required')); return; }
    setSaving('new');
    const data = {
      items: [{
        sourceValue: newRow.sourceValue,
        targetValue: newRow.targetValue || '',
        isDefault: newRow.isDefault,
        displayOrder: mappings.length + 1,
        isActive: true
      }]
    };
    AppConnectorService.createValueMappings(endpointId, profileId, ruleId, data)
      .then((r) => {
        setNewRow({ sourceValue: '', targetValue: '', isDefault: false });
        showToast(tr('appconnector.vm.added'), 'success');
        // Recarregar lista completa após adicionar
        loadMappings();
        if (onUpdate) onUpdate();
      })
      .catch(err => showToast(extractError(err)))
      .finally(() => setSaving(null));
  };

  const handleUpdate = (mapping) => {
    if (!mapping.sourceValue.trim()) { showToast(tr('appconnector.vm.error.source.required')); return; }
    setSaving(mapping.id);
    AppConnectorService.updateValueMapping(endpointId, profileId, ruleId, mapping.id, {
      sourceValue: mapping.sourceValue,
      targetValue: mapping.targetValue || '',
      isDefault: mapping.isDefault,
      displayOrder: mapping.displayOrder,
      isActive: mapping.isActive
    })
      .then(() => {
        showToast(tr('appconnector.vm.saved'), 'success');
        setDirtyIds(prev => { const next = new Set(prev); next.delete(mapping.id); return next; });
        if (onUpdate) onUpdate();
      })
      .catch(err => showToast(extractError(err)))
      .finally(() => setSaving(null));
  };

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const handleDelete = (vmId) => {
    setDeleteConfirmId(null);
    setSaving(vmId);
    AppConnectorService.deleteValueMapping(endpointId, profileId, ruleId, vmId)
      .then(() => {
        showToast(tr('appconnector.vm.removed'), 'success');
        setMappings(prev => prev.filter(m => m.id !== vmId));
        if (onUpdate) onUpdate();
      })
      .catch(err => showToast(extractError(err)))
      .finally(() => setSaving(null));
  };

  const handleSaveAll = async () => {
    const dirtyMappings = mappings.filter(m => dirtyIds.has(m.id));
    if (dirtyMappings.length === 0) { showToast(tr('appconnector.vm.no.changes'), 'info'); return; }
    setSavingAll(true);
    let successCount = 0;
    let errorCount = 0;
    for (const mapping of dirtyMappings) {
      try {
        await AppConnectorService.updateValueMapping(endpointId, profileId, ruleId, mapping.id, {
          sourceValue: mapping.sourceValue,
          targetValue: mapping.targetValue || '',
          isDefault: mapping.isDefault,
          displayOrder: mapping.displayOrder,
          isActive: mapping.isActive
        });
        successCount++;
        setDirtyIds(prev => { const next = new Set(prev); next.delete(mapping.id); return next; });
      } catch (err) {
        errorCount++;
      }
    }
    setSavingAll(false);
    if (errorCount === 0) showToast(tr('appconnector.vm.bulk.success', { count: successCount }), 'success');
    else showToast(tr('appconnector.vm.bulk.partial', { success: successCount, errors: errorCount }), 'warning');
    if (onUpdate) onUpdate();
  };

  const updateMapping = (index, field, value) => {
    const updated = [...mappings];
    updated[index] = { ...updated[index], [field]: value };
    setMappings(updated);
    if (updated[index].id) {
      setDirtyIds(prev => new Set(prev).add(updated[index].id));
    }
  };

  return (
    <div style={{ background: '#fafafa', borderRadius: '8px', padding: '16px', marginTop: '8px', border: '1px solid #e0e0e0' }}>
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {tr('appconnector.vm.title', { field: targetFieldName })}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}><CircularProgress size={20} /></div>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#888' }}>{tr('appconnector.vm.col.source')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '11px', textAlign: 'center', width: 40 }}>→</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#888' }}>{tr('appconnector.vm.col.target')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#888', textAlign: 'center', width: 70 }}>{tr('appconnector.vm.col.default')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#888', width: 90, textAlign: 'center' }}>{tr('appconnector.vm.col.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.map((m, idx) => (
                <TableRow key={m.id || idx} sx={{
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  position: 'relative',
                  opacity: saving === m.id ? 0.5 : 1,
                  pointerEvents: saving === m.id ? 'none' : 'auto',
                  backgroundColor: dirtyIds.has(m.id) ? '#fffde7' : 'transparent',
                  borderLeft: dirtyIds.has(m.id) ? '3px solid #f57f17' : 'none',
                }}>
                  <TableCell>
                    {saving === m.id && (
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 1 }}>
                        <CircularProgress size={20} />
                      </div>
                    )}
                    <TextField size="small" fullWidth value={m.sourceValue} variant="outlined"
                      onChange={e => updateMapping(idx, 'sourceValue', e.target.value)}
                      sx={{ '& input': { fontSize: '13px' } }} />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', color: '#8BC34A', fontWeight: 700, fontSize: '16px' }}>→</TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth value={m.targetValue} variant="outlined"
                      placeholder={tr('appconnector.vm.target.placeholder')}
                      onChange={e => updateMapping(idx, 'targetValue', e.target.value)}
                      sx={{ '& input': { fontSize: '13px' } }} />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Radio size="small" checked={m.isDefault || false}
                      onChange={() => {
                        const updated = mappings.map((item, i) => ({ ...item, isDefault: i === idx }));
                        setMappings(updated);
                      }} />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Tooltip title={tr('appconnector.vm.tooltip.save')}>
                      <IconButton size="small" onClick={() => handleUpdate(m)} disabled={saving === m.id}>
                        <Save size={14} color="#1565c0" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={tr('appconnector.common.delete')}>
                      <IconButton size="small" onClick={() => setDeleteConfirmId(m.id)} disabled={saving === m.id}>
                        <Trash2 size={14} color="#c62828" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {/* Nova linha para adicionar */}
              <TableRow sx={{ backgroundColor: '#f8faf5' }}>
                <TableCell>
                  <TextField size="small" fullWidth placeholder={tr('appconnector.vm.source.placeholder')} value={newRow.sourceValue}
                    onChange={e => setNewRow({ ...newRow, sourceValue: e.target.value })}
                    sx={{ '& input': { fontSize: '13px' } }} />
                </TableCell>
                <TableCell sx={{ textAlign: 'center', color: '#ccc', fontSize: '16px' }}>→</TableCell>
                <TableCell>
                  <TextField size="small" fullWidth placeholder={tr('appconnector.vm.col.target')} value={newRow.targetValue}
                    onChange={e => setNewRow({ ...newRow, targetValue: e.target.value })}
                    sx={{ '& input': { fontSize: '13px' } }} />
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Radio size="small" checked={newRow.isDefault}
                    onChange={() => {
                      const updated = mappings.map(item => ({ ...item, isDefault: false }));
                      setMappings(updated);
                      setNewRow({ ...newRow, isDefault: true });
                    }} />
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Tooltip title={tr('appconnector.vm.tooltip.add')}>
                    <IconButton size="small" onClick={handleAdd} disabled={!newRow.sourceValue.trim() || saving === 'new'}>
                      {saving === 'new' ? <CircularProgress size={14} /> : <Plus size={14} color="#8BC34A" />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {mappings.length === 0 && !loading && (
        <Alert severity="info" sx={{ mt: 1, fontSize: '12px' }}>
          {tr('appconnector.vm.empty')}
        </Alert>
      )}

      {/* Botão Salvar Todos */}
      {mappings.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, gap: 1, alignItems: 'center' }}>
          {dirtyIds.size > 0 && (
            <span style={{ fontSize: '12px', color: '#f57f17' }}>
              {tr('appconnector.vm.dirty', { count: dirtyIds.size })}
            </span>
          )}
          <Button
            variant="contained"
            size="small"
            disabled={dirtyIds.size === 0 || savingAll}
            startIcon={savingAll ? <CircularProgress size={14} color="inherit" /> : <Save size={14} />}
            onClick={handleSaveAll}
            sx={{ textTransform: 'none', borderRadius: '6px', fontSize: '12px' }}
          >
            {tr('appconnector.vm.save.all')}
          </Button>
        </Box>
      )}

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle sx={{ pb: 0 }}>{tr('appconnector.common.confirm.delete')}</DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Alert severity="warning" sx={{ borderRadius: '8px' }}>
            {tr('appconnector.vm.delete.confirm')}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmId(null)} sx={{ textTransform: 'none' }}>{tr('appconnector.common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirmId)} sx={{ textTransform: 'none' }}>{tr('appconnector.common.delete')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ValueMappingEditor;
